import React, { useState } from "react";
import Cookie from "js-cookie";
import api from "../services/api.service";
import { useNavigate } from "react-router-dom";
import { useCombinedStore } from "../store";
import * as AlfredoStyles from "../styles/components/RegisterModal";
import InputMask from "react-input-mask";
import { z } from "zod";
import { AddressAutocomplete } from "./AddressAutocomplete";
import UploadAvatar from "./uploadAvatar";
import midiaService from "../services/midia.service";

const RegisterModal: React.FC<{ setIsLoading: (loading: boolean) => void }> = ({
  setIsLoading,
}) => {
  const [error, setError] = useState<any>({});
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [userImageFile, setUserImageFile] = useState<File | null>(null);
  const [subsidiaryImageFile, setSubsidiaryImageFile] = useState<File | null>(
    null
  );
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<{
    user: number;
    subsidiary: number;
  }>({
    user: 0,
    subsidiary: 0,
  });

  const {
    setAuthUserZustand,
    setMessageZustand,
    selectedSection,
    setSelectedSection,
    registerDataZustand,
    setRegisterDataZustand,
  } = useCombinedStore((state) => {
    return {
      setAuthUserZustand: state.setAuthUserZustand,
      setMessageZustand: state.setMessageZustand,
      selectedSection: state.selectedSectionZustand || "Email",
      setSelectedSection: state.setSelectedSectionZustand,
      registerDataZustand: state.registerDataZustand,
      setRegisterDataZustand: state.setRegisterDataZustand,
    };
  });

  const handleValidation = (schema: z.Schema<any>, data: any) => {
    const result = schema.safeParse(data);
    if (!result.success) {
      setError(result.error.flatten().fieldErrors);
      return false;
    }
    return true;
  };

  const handleButtonClick = (schema: z.Schema<any>, nextSection: string) => {
    let isValid = handleValidation(schema, registerDataZustand);

    if (isValid) {
      setSelectedSection(nextSection);
    } else {
      // Exibir uma mensagem de erro aqui se necessário
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError((prevState: any) => {
      return {
        ...prevState,
        [e.target.name]: null,
      };
    });
    const updatedRegisterDataZustand = {
      ...registerDataZustand,
      [e.target.name]: e.target.value,
    };
    setRegisterDataZustand(updatedRegisterDataZustand);
  };

  const handleImageUpload = (
    image: string | File,
    type: "user" | "subsidiary"
  ) => {
    if (image instanceof File) {
      if (type === "user") {
        setUserImageFile(image);
      } else {
        setSubsidiaryImageFile(image);
      }
    } else {
      // Se for base64, converter para File (mantendo compatibilidade)
      const file = midiaService.base64ParaArquivo(
        image.startsWith("data:") ? image : `data:image/jpeg;base64,${image}`,
        `${type}-${Date.now()}.jpg`
      );
      if (type === "user") {
        setUserImageFile(file);
      } else {
        setSubsidiaryImageFile(file);
      }
    }
  };

  const uploadImagesToB2 = async () => {
    const imageUrls: { userLogoUrl?: string; subsidiaryLogoUrl?: string } = {};

    if (userImageFile) {
      try {
        setUploading(true);
        setUploadProgress((prev) => ({ ...prev, user: 0 }));

        // Simular progresso
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev.user >= 90) {
              clearInterval(progressInterval);
              return { ...prev, user: 90 };
            }
            return { ...prev, user: prev.user + 10 };
          });
        }, 200);

        const userImageUrl = await midiaService.enviar(userImageFile, "perfil");
        imageUrls.userLogoUrl = userImageUrl;

        clearInterval(progressInterval);
        setUploadProgress((prev) => ({ ...prev, user: 100 }));
      } catch (error) {
        console.error("Erro ao fazer upload da imagem do usuário:", error);
        throw new Error(
          "Falha no upload da imagem de perfil. Tente novamente."
        );
      }
    }

    if (subsidiaryImageFile) {
      try {
        setUploadProgress((prev) => ({ ...prev, subsidiary: 0 }));

        // Simular progresso
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev.subsidiary >= 90) {
              clearInterval(progressInterval);
              return { ...prev, subsidiary: 90 };
            }
            return { ...prev, subsidiary: prev.subsidiary + 10 };
          });
        }, 200);

        const subsidiaryImageUrl = await midiaService.enviar(
          subsidiaryImageFile,
          "unidade",
        );
        imageUrls.subsidiaryLogoUrl = subsidiaryImageUrl;

        clearInterval(progressInterval);
        setUploadProgress((prev) => ({ ...prev, subsidiary: 100 }));
      } catch (error) {
        console.error("Erro ao fazer upload da imagem da loja:", error);
        throw new Error("Falha no upload da imagem da loja. Tente novamente.");
      }
    }

    setUploading(false);
    return imageUrls;
  };

  const handleSubmit = async (schema: z.Schema<any>) => {
    setIsLoading(true);

    let isValid = handleValidation(schema, registerDataZustand);
    if (!isValid) {
      setIsLoading(false);
      return;
    }

    if (
      registerDataZustand.userPassword !==
      registerDataZustand.userPasswordConfirm
    ) {
      setMessageZustand("As senhas não coincidem", "error");
      setIsLoading(false);
      return;
    }

    try {
      // Primeiro, fazer upload das imagens para o B2
      let imageUrls: { userLogoUrl?: string; subsidiaryLogoUrl?: string } = {};

      if (userImageFile || subsidiaryImageFile) {
        try {
          imageUrls = await uploadImagesToB2();
        } catch (uploadError: any) {
          setMessageZustand(
            uploadError.message || "Erro ao fazer upload das imagens",
            "error"
          );
          setIsLoading(false);
          return;
        }
      }

      // Preparar dados para envio
      const registrationData = {
        ...registerDataZustand,
        ...imageUrls,
      };

      // Enviar para o backend
      const response = await api.post(
        "/registerWithFastOnboarding",
        registrationData
      );

      // Fazer login automaticamente
      const loginResponse = await api.post("/login", {
        userEmail: registerDataZustand.userEmail,
        userPassword: registerDataZustand.userPassword,
      });

      setAuthUserZustand(loginResponse.data);

      if (Cookie.get("refreshToken") !== undefined) {
        Cookie.remove("refreshToken");
      }
      Cookie.set("refreshToken", loginResponse.data.authUser.RefreshToken, {
        expires: 30,
        secure: true,
      });

      setIsLoading(false);
      navigate("/");
    } catch (error: any) {
      setIsLoading(false);
      if (error.response?.data?.message !== undefined) {
        setMessageZustand(error.response.data.message, "error");
      } else if (error.message !== undefined) {
        setMessageZustand(error.message, "error");
      }
    }
  };

  const emailSchema = z.object({
    userEmail: z
      .string()
      .email({ message: "Este endereço de email não parece certo..." }),
  });

  const userSchema = z.object({
    userName: z
      .string()
      .min(2, { message: "Nome deve conter ao menos 2 letras" })
      .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/i, {
        message: "Insira somente letras",
      }),
    userSurname: z
      .string()
      .min(2, { message: "Nome deve conter ao menos 2 letras" })
      .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/i, {
        message: "Insira somente letras",
      }),
    userSocialName: z
      .string()
      .min(2, { message: "Nome deve conter ao menos 2 letras" })
      .regex(/^[A-Za-zÀ-ÖØ-öø-ÿ\s\-']+$/i, {
        message: "Insira somente letras",
      }),
  });

  const subsidiarySchema = z.object({
    subsidiaryName: z
      .string()
      .min(2, { message: "Nome deve conter ao menos 2 letras" }),
    subsidiaryAddress: z.string(), // TODO: VALIDACAO
    subsidiaryTaxPayerID: z
      .string()
      .length(14, { message: " CNPJ deve conter 14 caracteres numéricos" })
      .regex(/^[-+]?[0-9]+$/, { message: "Insira somente números" }),
  });

  const passwordSchema = z
    .object({
      userPassword: z
        .string()
        .min(6, { message: "Senha deve conter ao menos 6 letras" }),
      userPasswordConfirm: z.string(),
    })
    .refine((data) => data.userPassword === data.userPasswordConfirm, {
      message: "As senhas não coincidem",
      path: ["userPasswordConfirm"],
    });

  const renderContent = () => {
    switch (selectedSection) {
      case "Email":
        return (
          <AlfredoStyles.EmailContent>
            <AlfredoStyles.ContentTitle $textAlign="flex-start">
              Inscreva-se gratuitamente.
            </AlfredoStyles.ContentTitle>
            <AlfredoStyles.ContentSubtitle $textAlign="center">
              Conte com nosso embarque acelerado e configure seu Alfredo em
              poucos minutos.
            </AlfredoStyles.ContentSubtitle>
            <AlfredoStyles.TextInput>
              <input
                type="text"
                name="userEmail"
                value={registerDataZustand.userEmail}
                onChange={handleChange}
                required
              />
              <label>
                {error.userEmail ?? "Digite seu email de trabalho."}
              </label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TermsText>
              Ao se inscrever, você concorda com
              <a href="/termos-e-condicoes"> Termos & Condições</a>,
              <a href="/politica-de-privacidade"> Política de Privacidade</a> &
              <a href="/acordo-de-processamento">
                {" "}
                Acordo de Processamento de Dados
              </a>
              , e a receber comunicações de marketing do Alfredo.
            </AlfredoStyles.TermsText>
            <AlfredoStyles.Button
              onClick={() => handleButtonClick(emailSchema, "Usuario")}
            >
              Continuar
            </AlfredoStyles.Button>
          </AlfredoStyles.EmailContent>
        );
      case "Usuario":
        return (
          <AlfredoStyles.UserContent>
            <AlfredoStyles.ContentTitle $textAlign="center">
              Seja Bem vindo ao Alfredo!
            </AlfredoStyles.ContentTitle>
            <AlfredoStyles.ContentSubtitle $textAlign="center">
              Crie seu perfil no Alfredo!
            </AlfredoStyles.ContentSubtitle>
            {uploading && uploadProgress.user > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0,0,0,0.8)",
                  color: "white",
                  padding: "var(--sp-8) var(--sp-16)",
                  borderRadius: "4px",
                  fontSize: "var(--fs-xs)",
                  zIndex: 10,
                }}
              >
                Upload: {uploadProgress.user}%
              </div>
            )}
            <UploadAvatar
              onImageConfirmed={(image) => handleImageUpload(image, "user")}
              imageType={1}
              initialImage=""
            />
            {userImageFile && (
              <div
                style={{
                  marginTop: "var(--sp-8)",
                  padding: "var(--sp-4) var(--sp-8)",
                  backgroundColor: "#e8f5e8",
                  borderRadius: "4px",
                  fontSize: "var(--fs-xs)",
                  color: "#2e7d32",
                  textAlign: "center",
                }}
              >
                ✅ Imagem selecionada
              </div>
            )}
            <AlfredoStyles.TextInput>
              <input
                type="text"
                name="userName"
                value={registerDataZustand.userName}
                onChange={handleChange}
                required
              />
              <label>{error.userName?.[0] ?? "Nome"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <input
                type="text"
                name="userSurname"
                value={registerDataZustand.userSurname}
                onChange={handleChange}
                required
              />
              <label>{error.userSurname?.[0] ?? "Sobrenome"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <input
                type="text"
                name="userSocialName"
                value={registerDataZustand.userSocialName}
                onChange={handleChange}
                required
              />
              <label>{error.userSocialName?.[0] ?? "Nome Social"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.ButtonWrapper>
              <AlfredoStyles.Button onClick={() => setSelectedSection("Email")}>
                Voltar
              </AlfredoStyles.Button>
              <AlfredoStyles.Button
                onClick={() => handleButtonClick(userSchema, "Loja")}
                disabled={uploading}
              >
                Continuar
              </AlfredoStyles.Button>
            </AlfredoStyles.ButtonWrapper>
          </AlfredoStyles.UserContent>
        );
      case "Loja":
        return (
          <AlfredoStyles.SubsidiaryContent>
            <AlfredoStyles.ContentTitle $textAlign="center">
              ...E onde fica a sua Loja?
            </AlfredoStyles.ContentTitle>
            <AlfredoStyles.ContentSubtitle $textAlign="center">
              Crie o perfil da sua Loja no Alfredo
            </AlfredoStyles.ContentSubtitle>
            {uploading && uploadProgress.subsidiary > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(0,0,0,0.8)",
                  color: "white",
                  padding: "var(--sp-8) var(--sp-16)",
                  borderRadius: "4px",
                  fontSize: "var(--fs-xs)",
                  zIndex: 10,
                }}
              >
                Upload: {uploadProgress.subsidiary}%
              </div>
            )}
            <UploadAvatar
              onImageConfirmed={(image) =>
                handleImageUpload(image, "subsidiary")
              }
              imageType={2}
              initialImage=""
            />
            {subsidiaryImageFile && (
              <div
                style={{
                  marginTop: "var(--sp-8)",
                  padding: "var(--sp-4) var(--sp-8)",
                  backgroundColor: "#e8f5e8",
                  borderRadius: "4px",
                  fontSize: "var(--fs-xs)",
                  color: "#2e7d32",
                  textAlign: "center",
                }}
              >
                ✅ Imagem da loja selecionada
              </div>
            )}
            <AlfredoStyles.TextInput>
              <AddressAutocomplete
                inputName="subsidiaryAddress"
                setError={setError}
              />
              <label>{error.subsidiaryAddress?.[0] ?? "Endereço"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <input
                type="text"
                name="subsidiaryName"
                value={registerDataZustand.subsidiaryName}
                onChange={handleChange}
                required
              />
              <label>{error.subsidiaryName?.[0] ?? "Nome"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                name="subsidiaryTaxPayerID"
                value={registerDataZustand.subsidiaryTaxPayerID}
                onChange={handleChange}
                required
              />
              <label>{error.subsidiaryTaxPayerID?.[0] ?? "CNPJ"}</label>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.ButtonWrapper>
              <AlfredoStyles.Button
                onClick={() => setSelectedSection("Usuario")}
              >
                Voltar
              </AlfredoStyles.Button>
              <AlfredoStyles.Button
                onClick={() => handleButtonClick(subsidiarySchema, "Senha")}
                disabled={uploading}
              >
                Continuar
              </AlfredoStyles.Button>
            </AlfredoStyles.ButtonWrapper>
          </AlfredoStyles.SubsidiaryContent>
        );
      case "Senha":
        return (
          <AlfredoStyles.PasswordContent>
            <AlfredoStyles.ContentTitle $textAlign="center">
              Adicione Segurança à sua conta
            </AlfredoStyles.ContentTitle>
            <AlfredoStyles.ContentSubtitle $textAlign="center">
              Escolha sua senha de acesso aos produtos Alfredo.
            </AlfredoStyles.ContentSubtitle>
            <AlfredoStyles.TextInput>
              <AlfredoStyles.PasswordWrapper>
                <input
                  type={showPassword ? "text" : "password"}
                  name="userPassword"
                  value={registerDataZustand.userPassword}
                  onChange={handleChange}
                  required
                />
                <label>
                  {error.userPassword?.[0] ?? "Escolha a sua senha"}
                </label>
                <AlfredoStyles.TogglePasswordButton
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <AlfredoStyles.IconLabel>
                      visibility
                    </AlfredoStyles.IconLabel>
                  ) : (
                    <AlfredoStyles.IconLabel>
                      visibility_off
                    </AlfredoStyles.IconLabel>
                  )}
                </AlfredoStyles.TogglePasswordButton>
              </AlfredoStyles.PasswordWrapper>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <AlfredoStyles.PasswordWrapper>
                <input
                  type={showPassword ? "text" : "password"}
                  name="userPasswordConfirm"
                  value={registerDataZustand.userPasswordConfirm}
                  onChange={handleChange}
                  required
                />
                <label>
                  {error.userPasswordConfirm?.[0] ?? "Confirme a sua senha"}
                </label>
                <AlfredoStyles.TogglePasswordButton
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <AlfredoStyles.IconLabel>
                      visibility
                    </AlfredoStyles.IconLabel>
                  ) : (
                    <AlfredoStyles.IconLabel>
                      visibility_off
                    </AlfredoStyles.IconLabel>
                  )}
                </AlfredoStyles.TogglePasswordButton>
              </AlfredoStyles.PasswordWrapper>
            </AlfredoStyles.TextInput>
            <AlfredoStyles.TextInput>
              <InputMask
                mask="+55 (99) 99999-9999"
                maskPlaceholder="_"
                name="userPhoneNumber"
                value={registerDataZustand.userPhoneNumber}
                onChange={handleChange}
                required
              />
              <label>{error.userPhoneNumber?.[0] ?? "Telefone"}</label>
            </AlfredoStyles.TextInput>

            {(userImageFile || subsidiaryImageFile) && (
              <div
                style={{
                  marginTop: "var(--sp-16)",
                  padding: "var(--sp-12)",
                  backgroundColor: "#e8f5e8",
                  border: "1px solid #4caf50",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <span style={{ color: "#2e7d32", fontSize: "var(--fs-md)" }}>
                  ✅ Imagens prontas para upload. Ao continuar, as imagens serão
                  enviadas para armazenamento em nuvem.
                </span>
              </div>
            )}

            <AlfredoStyles.ButtonWrapper>
              <AlfredoStyles.Button onClick={() => setSelectedSection("Loja")}>
                Voltar
              </AlfredoStyles.Button>
              <AlfredoStyles.Button
                onClick={() => handleSubmit(passwordSchema)}
                disabled={uploading}
              >
                {uploading ? "Enviando imagens..." : "Finalizar Cadastro"}
              </AlfredoStyles.Button>
            </AlfredoStyles.ButtonWrapper>
          </AlfredoStyles.PasswordContent>
        );
      default:
        return null;
    }
  };

  return renderContent();
};

export default RegisterModal;
